import { VoxPillButton } from "../VoxPillButton"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * Cinematic banner that promotes the currently-recording session above
 * the sessions grid. Mono uppercase overline ("RECORDING NOW · CURSE
 * OF STRAHD · SESSION 14") with a pulsing crimson dot, Cinzel display
 * title, body-typed stats line, and a right-aligned "Open table" CTA.
 *
 * Voxcanon-only; lives next to the VoxPillButton family of primitives.
 * Lexicanon retains its own teal/indigo HeroLive in MeetingList.
 */
export const VoxLiveHero = ({
  eyebrowPrimary,
  campaignName,
  sessionLabel,
  title,
  stats,
  onOpen,
  onStop,
  openLabel,
  stopLabel,
}: IProps): React.JSX.Element => {
  const visibleStats = (stats ?? []).filter((s): s is React.ReactNode => s !== null && s !== undefined && s !== false)

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.eyebrow}>
          <span className={classes.eyebrowDot} aria-hidden="true" />
          <span>{eyebrowPrimary}</span>
          {campaignName && (
            <>
              <span className={classes.eyebrowSep}>·</span>
              <span>{campaignName}</span>
            </>
          )}
          {sessionLabel && (
            <>
              <span className={classes.eyebrowSep}>·</span>
              <span>{sessionLabel}</span>
            </>
          )}
        </div>
        <h2 className={classes.title}>{title}</h2>
        {visibleStats.length > 0 && (
          <div className={classes.stats}>
            {visibleStats.map((s, i) => (
              // Stats are arbitrary ReactNode (not key-able) and render as a
              // fixed, non-reordering line — index is a stable key here.
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {i > 0 && <span className={`${classes.statsDot} lex-dot`} aria-hidden="true" />}
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={classes.actions}>
        {onStop && stopLabel && (
          <VoxPillButton variant="grimoire" onClick={onStop}>
            {stopLabel}
          </VoxPillButton>
        )}
        <VoxPillButton variant="arcane" onClick={onOpen}>
          {openLabel}
        </VoxPillButton>
      </div>
    </div>
  )
}
